import moment from "moment";
import { APIV4_BASE_URL, API_BASE_URL, apiClient, newApiClient, newUserClient, uhtCashClient, challengeClient, genericApiClient, dermaClient, pasiClient, nhsClient, foodInsightsClient } from "./apiClient";
import { getAppParams } from "./timespam";

export const lifestyleApi = {
  getHraData(goqiiUserId) {
    return newApiClient.post("/hra", {
      goqiiUserId,
      flag: 1,
      callingFrom: "web",
    });
  },

  weightData(goqiiUserId) {
    return newApiClient.post("/weight", {
      goqiiUserId,
      flag: 1,
      callingFrom: "web",
    });
  },

  foodData(goqiiUserId) {
    return newApiClient.post("/food", {
      goqiiUserId,
      flag: 1,
      callingFrom: "web",
    });
  },

  lifepyramidData(goqiiUserId) {
    return newApiClient.post("/lifestylevalues", {
      goqiiUserId,
      flag: 1,
      callingFrom: "web",
    });
  },

  glucoseData(goqiiUserId) {
    return newApiClient.post("/glucose", {
      goqiiUserId,
      flag: 1,
      callingFrom: "web",
    });
  },

  agingpaceData(goqiiUserId) {
    return newApiClient.post("/fetch_pace_aging", {
      goqiiUserId,
      flag: 1,
      callingFrom: "web",
    });
  },

  safebarData(goqiiUserId) {
    return newApiClient.post("/safe_bar", {
      goqiiUserId,
      callingFrom: "web",
    });
  },

  HealthGraphData(goqiiUserId, days) {
    return newApiClient.post("/graph_v1", {
      goqiiUserId,
      days,
      flag: 1,
      callingFrom: "web",
    });
  },

  mygoqiiData(goqiiUserId) {
    return newApiClient.post("/lifestyle_comparison", {
      goqiiUserId,
      flag: 1,
      callingFrom: "web",
    });
  },

  stepgraphData(goqiiUserId) {
    return newApiClient.post("/stepdata", {
      goqiiUserId,
      callingFrom: "web",
    });
  },

  healthtimelineData(goqiiUserId) {
    return newApiClient.post("/journey_timeline", {
      goqiiUserId,
      callingFrom: "web",
    });
  },

  agecardData(goqiiUserId) {
    return newApiClient.post("/agecard", {
      goqiiUserId,
      callingFrom: "web",
    });
  },

  agecompareData(goqiiUserId) {
    return newUserClient.post("/age_reversor_profile", {
      uuid: goqiiUserId,
      callingFrom: "web",
    });
  },

  agegenData(goqiiUserId) {
    return newUserClient.post("/age_reversor_profile", {
      uuid: goqiiUserId,
      callingFrom: "web",
    });
  },

  taskDataByType(goqiiUserId, type) {
    return newApiClient.post(
      `${API_BASE_URL}/lifestyle/fetch_lifestyle_plans`,
      {
        goqiiUserId,
        type,
        flag: 1,
        callingFrom: "web",
      }
    );
  },

  challengesCardData(goqiiUserId, type, extraParams = {}) {
    return newApiClient.post(
      `${API_BASE_URL}/activechallenge/challenge_listing`,
      {
        goqiiUserId,
        type,
        flag: 1,
        callingFrom: "web",
        ...extraParams,
      }
    );
  },

  goqiiData(goqiiUserId) {
    return newApiClient.post(
      `${API_BASE_URL}/lifestyle/fetch_coach_details`,
      {
        goqiiUserId,
        callingFrom: "web",
      }
    );
  },

  getUserInsurnceDetails(goqiiUserId) {
    return newApiClient.post(
      `${API_BASE_URL}/lifestyle/fetch_insurance_details`,
      {
        goqiiUserId,
        callingFrom: "web",
      }
    );
  },

  // Gates the SleepScore card: true only if the user has a tracker (imei)
  // on file. Response shape: { data: { sleep_insight: boolean } } — the
  // backend doesn't set a top-level `code` on success, only on error.
  checkSleepInsight(goqiiUserId) {
    return newApiClient.post("/check_sleep_insight", { goqiiUserId });
  },

  // GET to apiv4 — matches the Angular app's endpoint and HTTP method
  nutritionWordcloud(goqiiUserId, mealType) {
    return apiClient.get("/wordcloud", {
      params: { flag: 1, goqiiUserId, mealType },
    });
  },

  nutritionTimeline(goqiiUserId, mealType) {
    return apiClient.get("/timeline", {
      params: { flag: 1, goqiiUserId, mealType },
    });
  },

  nutritionWaterGraph(goqiiUserId) {
    return apiClient.get("/watergraph", {
      params: { flag: 1, goqiiUserId },
    });
  },

  nutritionMonthlyCalendar(goqiiUserId) {
    return apiClient.get("/monthly_calendar", {
      params: { flag: 1, goqiiUserId },
    });
  },

  // Ported from the standalone food app's /weekly-calories route
  // (food/src/components/foodInsights/Weeklycalories.js) — different base
  // path (foodinsights, not lifestylecard), so pass the full URL.
  weeklyCalories(goqiiUserId) {
    return apiClient.get(`${APIV4_BASE_URL}/foodinsights/calories`, {
      params: { goqiiUserId },
    });
  },

  // Ported from goqii-user-referral's FoodLogInsight page — per-day
  // nutrients/macros/calories breakdown, plus each metric's last-7-days
  // trend + target (proteinTotal/proteinDates/proteingm etc.), all in one
  // response.
  foodLogNutrients(date) {
    return foodInsightsClient.get("/nutrients", { params: { date } });
  },

  // Macros tab only — lazily fetched the first time that tab is viewed
  // (matches the live page), powers "Last 7 days Macronutrients Ratio %".
  // Confirmed shape: { caloriesDates, nutrientTotals: [[proteinPct, fatPct,
  // carbsPct], ...one per day], lifestyle }.
  foodLogNutrientsMacros(date) {
    return foodInsightsClient.get("/nutrients_macros", { params: { date, filterValue: "" } });
  },

  // Matches the live page's analytics call (apiv4.goqii.com/event/add_events)
  // fired once per tab view — same payload shape, reusing foodInsightsClient
  // since it's the same query-param auth scheme. `lifestyle` mirrors the live
  // page pulling it from whatever food-log response has already loaded
  // (defaults to " " before any response returns, e.g. on first paint).
  logFoodInsightEvent(tabName, lifestyle = " ") {
    const { goqiiUserId, appType, appVersion } = getAppParams();
    const payload = [
      {
        userId: goqiiUserId,
        goqiiAnalyticId: "0",
        analyticsId: Date.now(),
        eventTime: moment().format("YYYY-MM-DD HH:mm:ss"),
        type: "event",
        eventName: "Food",
        eventData: {
          CardItemType: "",
          Action: tabName,
          CardKeyWord: "",
          ItemType: "",
          ItemName: "",
          Lifestyle: lifestyle,
          Page: "FoodNutrientInsights",
          ItemModule: "QuantifiedFood",
          Source: "Direct",
          ItemExperiment: "Default",
        },
        additionalInfo: {
          AppType: appType,
          AppVersion: appVersion,
          DeviceIdentifier: null,
          OSVersion: null,
        },
      },
    ];
    return foodInsightsClient.get(`${APIV4_BASE_URL}/event/add_events`, {
      params: { data: JSON.stringify(payload) },
    });
  },

  lifestyleEliteCard(goqiiUserId) {
    return newApiClient.post("/lifestylev2", {
      goqiiUserId,
      flag: 0,
      callingFrom: "web",
    });
  },

  graphV2Data(goqiiUserId, compareWithUserId) {
    return newApiClient.post("/graph_v2", {
      goqiiUserId,
      compareWithUserId,
      flag: 1,
      callingFrom: "web",
    });
  },

  //Games APIs — no lang, uses original apiClient

  fetchGamesV2({ uid, goqiiUserId, goqiiAccessToken, apiKey, nonce, signature, appVersion, appType }) {
    return apiClient.post(
      `${API_BASE_URL}/goqiiapigames/fetch_games_v2`,
      { userId: uid, goqiiUserId, goqiiAccessToken, apiKey, nonce, signature, appVersion, appType, callingFrom: "web" }
    );
  },

  fetchGameDetails(goqiiUserId, gid) {
    return apiClient.post(
      `${APIV4_BASE_URL}/lifestylecard/fetch_game_details`,
      {
        goqiiUserId,
        callingFrom: "web",
        gid,
      }
    );
  },

  initialGameScore(goqiiUserId, gid) {
    return apiClient.post(
      `${APIV4_BASE_URL}/lifestylecard/initial_game_score`,
      {
        goqiiUserId,
        callingFrom: "web",
        gid,
        totalQuestions: 0,
        correctAnswers: 0,
        wrongAnswers: 0,
        longestStreak: 0,
        fastResponse: 0,
        gameScore: 0,
        state: "initial",
        cid: "",
        groupName: "",
      }
    );
  },

  // UHT Cash Details
  fetchUhtCashDetails({ userId, paginationTab, pagination }) {
    return uhtCashClient.post("/uht_goqii_cash", {
      userId,
      paginationTab,
      pagination,
    });
  },

  saveGameScore(
    goqiiUserId,
    gid,
    totalQuestions,
    correctAnswers,
    wrongAnswers,
    longestStreak,
    fastResponse,
    score,
    cid,
    lastInsertedId,
    scoreOutOf100 = 0
  ) {
    return apiClient.post(
      `${APIV4_BASE_URL}/lifestylecard/save_game_score`,
      {
        goqiiUserId,
        callingFrom: "web",
        gid,
        totalQuestions,
        correctAnswers,
        wrongAnswers,
        longestStreak,
        fastResponse,
        gameScore: score,
        cid,
        lastInsertedId,
        scoreOutOf100,
      }
    );
  },

  fetchChallengeRules({ goqiiUserId, goqiiAccessToken, nonce, apiKey, signature, appType, appVersion, challengeId }) {
    return challengeClient.get("/fetch_generic_challenge_info", {
      params: { goqiiUserId, goqiiAccessToken, nonce, apiKey, signature, appType, appVersion, challengeId },
    });
  },

  fetchPageByFeatures(pageId = 1, faiId = 666) {
    return genericApiClient.post("/fetch_page_by_features_v2", {
      pageId,
      fai: JSON.stringify({ type: "genericCards", id: faiId }),
    });
  },

  fetchHealthAssessmentAccess(goqiiUserId) {
    return genericApiClient.post("/allow_user_ids_health_assetment", { goqiiUserId });
  },

  fetchDermaDoctor() {
    return dermaClient.post("fetch_pfizer_doctor_details");
  },

  fetchDermaUser() {
    return dermaClient.post("fetch_user_details");
  },

  logSkinMood({ itchingValue, painValue, rednessValue, sleepValue }) {
    return dermaClient.post("log_skin_mood", {
      itchingValue,
      painValue,
      rednessValue,
      sleepValue,
    });
  },

  fetchSkinMood() {
    return dermaClient.post("fetch_skin_mood");
  },

  logFlareUp({ sliderValue, selectedParameters }) {
    return pasiClient.post("log_flare_up", { sliderValue, selectedParameters });
  },

  logMood({ baseEmotion, commonReason, anythingElse }) {
    return dermaClient.post("log_mood", { baseEmotion, commonReason, anythingElse });
  },

  logPsqi({ goqiiUserId, logQuestions }) {
    return dermaClient.post("log_psqi", { goqiiUserId, logQuestions });
  },

  logEasi({ goqiiUserId, formData }) {
    return dermaClient.post("log_easi", { goqiiUserId, formData });
  },

  logPasi({ goqiiUserId, regions }) {
    return pasiClient.post("log_pasi", { goqiiUserId, regions });
  },

  logBsa({ selectedStates, totalPercentage }) {
    return dermaClient.post("log_bsa", { selectedStates, totalPercentage });
  },

  fetchFlareUpStats() {
    return pasiClient.post("fetch_flare_up");
  },

  fetchBsaScore() {
    return dermaClient.post("fetch_bsa_score");
  },

  fetchPsqiScore() {
    return dermaClient.post("fetch_psqi_score");
  },

  fetchEasiScore() {
    return dermaClient.post("fetch_easi_score");
  },

  fetchPasiScore() {
    return pasiClient.post("fetch_pasi_score");
  },

  fetchDlqiScore() {
    return dermaClient.post("fetch_dlqi_score");
  },

  fetchDlqiQuestions() {
    return dermaClient.post("fetch_questions");
  },

  logDlci({ goqiiUserId, logQuestions, score }) {
    return dermaClient.post("log_dlci", { goqiiUserId, logQuestions, score });
  },

  fetchEmotionalGraph({ goqiiUserId }) {
    return dermaClient.post("fetch_emotional_graph", { goqiiUserId });
  },

  saveTreatmentPlan({ attrmasterid, attrsubid, isActive, selectedDate }) {
    return dermaClient.post("save_treatment_plan", { attrmasterid, attrsubid, isActive, selectedDate });
  },

  fetchTreatmentPlan({ selectedDate }) {
    return dermaClient.post("fetch_treatment_plan", { selectedDate });
  },

  getSkinDetails({ goqiiUserId }) {
    return dermaClient.post("fetch_skin_details", { goqiiUserId });
  },

  fetchWFIRSSQuestions() {
    return nhsClient.post("fetch_wfirss_questions");
  },

  logWFIRSS({ logQuestions, score, componentScore }) {
    return nhsClient.post("log_wfirss", { logQuestions, score, componentScore });
  },

  fetchWFIRSSScore() {
    return nhsClient.post("fetch_wfirss_score");
  },

  fetchWFIRSSReports() {
    return nhsClient.post("fetch_wfirss_reports");
  },

  removeWFIRSSReport({ scoreId }) {
    return nhsClient.post("remove_wfirss_reports", { scoreId });
  },

  counsellorSession() {
    return dermaClient.post("counsellor_session");
  },

  fetchMonthlyReport({ goqiiUserId }) {
    return dermaClient.post("fetch_monthly_report", { goqiiUserId });
  },

  logIntensitySigns({ goqiiUserId, formData }) {
    return dermaClient.post("log_intensity_signs", { goqiiUserId, ...formData });
  },

  logIntensitySymptoms({ itchIntensity, sleeplessness }) {
    return dermaClient.post("log_intensity_symptoms", { itchIntensity, sleeplessness });
  },

  fetchScore() {
    return dermaClient.post("fetch_score");
  },

  fetchScoradScore() {
    return dermaClient.post("fetch_scorad_score");
  },

  scoradLogin({ id, nhsNumber, mobile }) {
    return dermaClient.post("scorad_login", { id, nhsNumber, mobile });
  },

  fetchSignSymptomsScore({ scoreId }) {
    return dermaClient.post("fetch_sign_symptoms_score", { scoreId });
  },

  linkNavigate({ id }) {
    return dermaClient.post("link_navigate", { id });
  },

  fetchBsaDetails() {
    return dermaClient.post("fetch_bsa_details");
  },
};
